import { z } from 'zod';

import {
  DynamicArtifactTemplate,
  DynamicSummary,
  TextFieldBlock,
} from '@eluve/llm-outputs';
import {
  convertMarkdownToPlainText,
  convertMarkdownToPlainTextRecursively,
  humanReadableDateAndTimeWithDashes,
  splitFullName,
} from '@eluve/utils';

import { AppointmentModel } from '../../models/appointment';
import { ChartType } from '../../models/chart';
import { PatientType } from '../../models/patient';
import { UserType } from '../../models/user';
import {
  BillingCode,
  PatientEhrUrlArgs,
  VendorDataMapper,
  VendorSyncConfig,
} from '../vendor-data-mapper';

import { logo } from './logo';
import {
  SiloamSignedChartSchema,
  appointmentSchema,
  appointmentsSchema,
  chartSchema,
} from './types';

const userSchema = z
  .object({
    id: z.string(),
    fullName: z.string(),
    specialty: z.string().nullish(),
  })
  .passthrough();

export type EnvConfig = {
  web: string;
  api: string;
  sourceId: string;
};

const ENV_CONFIGURATIONS: EnvConfig[] = [
  {
    web: 'dev-genai-soap.siloamhospitals.com',
    api: 'https://siloam-emr-be-uat.siloamhospitals.com:5511',
    sourceId: '3985g01971bf',
  },
  {
    web: 'genai-soap.siloamhospitals.com',
    api: 'https://siloam-emr-be.siloamhospitals.com:5511',
    sourceId: '1985g98092cf',
  },
];

export class SiloamVendorDataMapper implements VendorDataMapper {
  /**
   * Resolves the environment configuration (API host, source ID) based on the web domain.
   * Supports both UAT (dev-genai-soap) and production (genai-soap) environments.
   */
  getEnvConfig = (domain: string) => {
    const envConfig = ENV_CONFIGURATIONS.find(
      (config) => config.web === domain,
    );
    if (!envConfig) {
      throw new Error(`Unsupported SILOAM domain: ${domain}`);
    }
    return envConfig;
  };

  getSourceId = (domain: string) => {
    return this.getEnvConfig(domain).sourceId;
  };

  getApiBaseUrl = (domain: string) => {
    return this.getEnvConfig(domain).api;
  };

  /**
   * Builds the URL for fetching appointment details from Siloam EMR.
   * Used by the extension to retrieve patient and encounter information.
   */
  buildAppointmentDetailsUrl = (domain: string, encounterId: string) => {
    const { api, sourceId } = this.getEnvConfig(domain);
    return `${api}/api/v1/appointments/${sourceId}/${encounterId}`;
  };

  /**
   * Builds the URL for marking an appointment as synced in Siloam EMR.
   * Called after extension-based recordings to confirm the note was synced.
   */
  buildSyncConfirmationUrl = (domain: string, encounterId: string) => {
    const { api, sourceId } = this.getEnvConfig(domain);
    return `${api}/api/v2/soapai/${sourceId}/${encounterId}`;
  };

  /**
   * Builds the URL for generating a magic link to access an encounter.
   * Used for deep-linking users directly into the Siloam EMR encounter page.
   */
  buildMagicLinkUrl = (domain: string, encounterId: string) => {
    const { api, sourceId } = this.getEnvConfig(domain);
    return `${api}/api/v1/magic-link/${sourceId}/${encounterId}`;
  };

  isMockAppointment = (encounterId: string | null) => {
    return encounterId?.startsWith('eluve-test');
  };

  isSoapPage = (url?: string) => {
    return Boolean(url?.toLowerCase().includes('/form/soap/'));
  };

  isEluveDomain = (url: string) => {
    return url.includes('eluve.com') || url.includes('eluve.dev');
  };

  convertLlmOutputToMatchingEhrSignedNoteFormat({
    llmOutputContent,
  }: {
    llmOutputContent: unknown;
    template: DynamicArtifactTemplate;
  }) {
    return convertMarkdownToPlainTextRecursively(llmOutputContent) as Record<
      string,
      unknown
    >;
  }

  convertRawDataToJsonNote({
    rawData,
    template,
  }: {
    rawData: unknown;
    template: DynamicArtifactTemplate;
  }): Record<string, unknown> {
    if (!template) {
      // We'll ignore this for Siloam since it has its own structure
    }

    const parsed = SiloamSignedChartSchema.parse(rawData);

    return {
      plan: parsed.soap.Planning,
      objective: parsed.soap.Objective,
      assessment: parsed.soap.Assessment,
      subjective: {
        anamnesis: parsed.soap.SubjectiveAnamnesis,
        chiefComplaint: parsed.soap.SubjectiveChiefComplaint,
      },
    };
  }
  convertChartToSummary({
    data,
    template,
  }: {
    data: unknown;
    template: DynamicArtifactTemplate;
  }): {
    summary: DynamicSummary;
    billingCodes: BillingCode[];
  } {
    const type = 'DYNAMIC' as const;
    if (!template) {
      throw new Error('Missing template for Siloam chart conversion');
    }
    const parsed = SiloamSignedChartSchema.parse(data);
    const blocks = Object.entries(parsed.soap).map(([key, value]) => {
      return {
        type: 'text',
        key: key,
        label: key,
        text: convertMarkdownToPlainText((value as string) ?? ''),
      } as TextFieldBlock;
    });

    const assessmentContent =
      blocks.find((b) => b.key === 'Assessment')?.text ?? '';
    const codeRegex = /ICD-10 Code:\s*([A-Z0-9.]+)\s*-\s*([^\n\r\\]+)/g;
    const billingCodes = [];
    let match;
    while ((match = codeRegex.exec(assessmentContent)) !== null) {
      billingCodes.push({
        code: match[1] ?? '',
        description: match[2]?.trim().split('\\n')[0] ?? '',
      });
    }
    return { summary: { type, blocks }, billingCodes };
  }

  getAppointments(data: unknown): AppointmentModel[] {
    const appointments = appointmentsSchema.parse(data);
    return appointments.map((appointment) => {
      const fullName = splitFullName(appointment.patient.fullName);
      const startTime =
        appointment.startDate ??
        appointment.admissionDate ??
        new Date().toISOString();
      const endTime = appointment.endDate ?? '';
      return {
        externalId: appointment.id,
        name:
          appointment.name ??
          `New Session ${humanReadableDateAndTimeWithDashes()}`,
        description: '',
        externalPatientId: appointment.patient.id,
        firstName: fullName.firstName,
        lastName: fullName.lastName,
        startTime,
        endTime,
        rawEhrData: {},
        isArchived: false,
        isImportable: true,
        status: undefined,
        sessionType: undefined,
      };
    });
  }

  getUser(data: unknown): UserType {
    const user = userSchema.parse(data);
    const email = `${user.id}-eluve@siloamhospitals.com`;
    const fullName = splitFullName(user.fullName);
    return {
      id: user.id,
      email,
      ...fullName,
      specialty: user.specialty ?? undefined,
    };
  }

  getPatient(data: unknown): PatientType | null {
    const { patient } = appointmentSchema.parse(data);
    const fullName = splitFullName(patient.fullName);
    return {
      externalPatientId: patient.id,
      firstName: fullName.firstName,
      lastName: fullName.lastName,
      dateOfBirth: patient.birthDate ?? null,
      email: null,
      homePhone: null,
      cellPhone: null,
      workPhone: null,
      gender: patient.gender ?? null,
      rawData: patient as Record<string, unknown>,
    };
  }

  getPatientEhrUrl(data: PatientEhrUrlArgs): string {
    return '';
  }

  getLogo(): string {
    return logo;
  }

  getHomeUrl(domain: string): string {
    return 'https://www.siloamhospitals.com';
  }

  getChart(
    data: unknown,
    additionalFields?: Record<string, unknown>,
  ): ChartType {
    const parsed = chartSchema.parse(data);
    return {
      externalPatientId: parsed.PatientId,
      externalChartId: parsed.EncounterId,
      externalAppointmentId: parsed.EncounterId,
      signedAt: null,
      rawData: data as Record<string, unknown>,
    };
  }

  getSyncConfig(): VendorSyncConfig {
    return {
      isImportEnabled: false,
      isSyncEnabled: true,
      canSignNoteInEhr: false,
      canSyncWithManualChartUrl: false,
      shouldDisplayConfirmationBeforeSync: false,
      canSyncNoteToEhr: ({ externalAppointmentId }) => {
        if (!externalAppointmentId) {
          return {
            canSync: false,
            reason: 'Selected appointment was not imported from EHR',
          };
        }
        return { canSync: true };
      },
    };
  }

  getVendorSchema = () => {
    const secretSchema = z.object({
      'Auth Token': z.string(),
      'SOAP URL': z
        .string()
        .url()
        .describe('Additional full URL where SOAP note will be synced to'),
    });
    return { secretSchema };
  };
}
